import { useCallback, useEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CheckCircle2, RefreshCw, RotateCcw, UserMinus } from 'lucide-react'
import { toast } from 'sonner'
import { usePersistentState } from '@/hooks/usePersistentState'
import { requestProxyNoThrottle } from '@/lib/request'

interface IpCacheRow {
  id?: string
  _id?: string
  account_name?: string
  address?: string
  apk_key_duration?: number
  province?: string
  city?: string
  content?: string
  is_rd?: boolean | string | number
  protocol?: string
  requested_province?: string
  requested_city?: string
  requested_code?: string
  is_verified?: boolean | string | number
  verification_status?: string
  validation_attempts?: number | string
  location_verified?: boolean | string | number
  location_matched?: boolean | string | number
  location_region_consistent?: boolean | string | number
  location_region_matched?: boolean | string | number
  location_check_source?: string
  location_check_ip?: string
  location_check_city_code?: string
  location_check_isp?: string
  location_check_sources_checked?: number | string
  location_check_sources_matched?: number | string
  location_check_sources_total?: number | string
  location_region_sources_matched?: number | string
  location_requested_region_sources_matched?: number | string
  location_confidence?: string
  location_check_results?: unknown[]
  location_check_errors?: unknown[]
  location_remark?: string
  last_validation_time?: string
  verified_time?: string
  create_time?: string
  update_time?: string
}

interface QueryResponse {
  list?: IpCacheRow[]
  total?: number
}

type IpCacheAction = 'pass' | 'remove'

interface PendingAction {
  action: IpCacheAction
  row: IpCacheRow
}

type FilterValue = string | boolean

const toBoolean = (value: unknown) => value === true || value === 'true' || value === 1

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== ''

const displayValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return '-'
  return String(value)
}

const verificationStatusMap: Record<string, { label: string; className: string }> = {
  pending: {
    label: '待验证',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  },
  verified: {
    label: '已验证',
    className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  },
  verified_mismatch: {
    label: '已验证，位置不一致',
    className: 'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300',
  },
  failed: {
    label: '验证失败，待重试',
    className: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300',
  },
}

const getVerificationStatus = (row: IpCacheRow) => {
  if (row.verification_status) return row.verification_status

  if (hasValue(row.is_verified)) {
    if (toBoolean(row.is_verified)) {
      return toBoolean(row.location_matched) ? 'verified' : 'verified_mismatch'
    }
    return 'failed'
  }

  if (hasValue(row.location_verified)) {
    if (toBoolean(row.location_verified)) {
      return toBoolean(row.location_matched) ? 'verified' : 'verified_mismatch'
    }
    return 'failed'
  }

  return 'legacy'
}

const getEffectiveVerified = (row: IpCacheRow) => {
  if (hasValue(row.is_verified)) return row.is_verified
  return row.location_verified
}

const actionConfig: Record<IpCacheAction, { path: string; title: string; description: string }> = {
  pass: {
    path: '/common/apis/v1/ip_cache_pass',
    title: '标记通过',
    description: '将这条 IP 缓存的 location_matched 设为 true。',
  },
  remove: {
    path: '/common/apis/v1/ip_cache_remove',
    title: '追加移除后缀',
    description: '将这条 IP 缓存的 account_name 追加 _remove 后缀。',
  },
}

const locationText = (province?: string, city?: string, code?: string) => {
  const name = [province, city].filter(Boolean).join('-')
  if (!name && !code) return '-'
  return code ? `${name || '-'} (${code})` : name
}

const rowKey = (row: IpCacheRow, index: number) => {
  return [
    row.account_name,
    row.id || row._id || row.content || row.location_check_ip || row.create_time,
    index,
  ].filter(Boolean).join('-')
}

const getPageData = async (
  pageNum: number,
  pageSize: number,
  filters: Record<string, FilterValue>,
) => {
  return requestProxyNoThrottle<QueryResponse>('/common/apis/v1/query_db_by_customer', {
    collect: 'ip_cache',
    page_size: pageSize,
    page_num: pageNum,
    query_filter: filters,
  })
}

export default function IpCache() {
  const [rows, setRows] = useState<IpCacheRow[]>([])
  const [pageSize, setPageSize] = usePersistentState('admin:ip-cache:page-size', 20)
  const [pageNum, setPageNum] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [actionLoadingKey, setActionLoadingKey] = useState('')
  const [filters, setFilters, clearFilters] = usePersistentState<Record<string, FilterValue>>(
    'admin:ip-cache:filters:v2',
    {},
  )
  const latestRequestRef = useRef(0)
  const inFlightLoadKeyRef = useRef('')

  const getFilterText = (field: string) => {
    const value = filters[field]
    return typeof value === 'string' ? value : ''
  }

  const setFilterValue = (field: string, value: FilterValue | undefined | null) => {
    setPageNum(1)
    setFilters((current) => {
      const next = { ...current }
      if (value === undefined || value === null || value === '') {
        delete next[field]
      } else {
        next[field] = value
      }
      return next
    })
  }

  const resetFilters = () => {
    clearFilters()
    setPageNum(1)
  }

  const fetchData = useCallback(async () => {
    const loadKey = JSON.stringify({ filters, pageNum, pageSize })
    if (inFlightLoadKeyRef.current === loadKey) return
    inFlightLoadKeyRef.current = loadKey

    const requestId = ++latestRequestRef.current
    setLoading(true)

    try {
      const resp = await getPageData(pageNum, pageSize, filters)

      if (requestId !== latestRequestRef.current) return

      if (!resp || resp.code !== 0) {
        setRows([])
        setTotal(0)
        toast.error(resp?.msg || 'IP Cache 查询失败')
        return
      }

      const totalCount = resp.data?.total || 0
      setTotal(totalCount)

      const nextTotalPages = Math.max(1, Math.ceil(totalCount / pageSize))
      if (pageNum > nextTotalPages) {
        setPageNum(nextTotalPages)
        return
      }

      setRows(resp.data?.list || [])
    } catch (error) {
      console.error('Failed to fetch ip_cache:', error)
      if (requestId === latestRequestRef.current) {
        setRows([])
        setTotal(0)
      }
      toast.error('IP Cache 查询失败')
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false)
      }
      if (inFlightLoadKeyRef.current === loadKey) {
        inFlightLoadKeyRef.current = ''
      }
    }
  }, [filters, pageNum, pageSize])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleConfirmAction = async () => {
    if (!pendingAction) return

    const { action, row } = pendingAction
    if (!row.id) {
      toast.error('该记录没有 id，无法更新')
      return
    }

    const config = actionConfig[action]
    const loadingKey = `${action}:${row.id}`
    setActionLoadingKey(loadingKey)

    try {
      const resp = await requestProxyNoThrottle<IpCacheRow>(config.path, { id: row.id })
      if (!resp || (typeof resp.code === 'number' && resp.code !== 0)) {
        toast.error(resp?.msg || `${config.title}失败`)
        return
      }

      toast.success(`${config.title}成功`)
      setPendingAction(null)
      await fetchData()
    } catch (error) {
      console.error(`Failed to ${action} ip_cache:`, error)
      toast.error(`${config.title}失败`)
    } finally {
      setActionLoadingKey('')
    }
  }

  const renderVerificationStatus = (row: IpCacheRow) => {
    const status = getVerificationStatus(row)
    const statusInfo = verificationStatusMap[status]
    const effectiveVerified = getEffectiveVerified(row)

    return (
      <div className="space-y-2">
        {statusInfo ? (
          <Badge variant="outline" className={statusInfo.className}>
            {statusInfo.label}
          </Badge>
        ) : (
          <Badge variant="outline">
            {status === 'legacy' ? '旧数据' : status}
          </Badge>
        )}
        <div className="space-y-1 text-xs text-muted-foreground">
          <div>完成验证：{hasValue(effectiveVerified) ? (toBoolean(effectiveVerified) ? '是' : '否') : '未记录'}</div>
          <div>尝试次数：{displayValue(row.validation_attempts)}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">IP Cache</h1>
            <p className="text-sm text-muted-foreground mt-1">
              查询 ip_cache，固定使用 VITE_PROXY_URL
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">账号</span>
              <Input
                className="w-[140px]"
                placeholder="account_name"
                value={getFilterText('account_name')}
                onChange={(event) => setFilterValue('account_name', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">请求省份</span>
              <Input
                className="w-[140px]"
                placeholder="requested_province"
                value={getFilterText('requested_province')}
                onChange={(event) => setFilterValue('requested_province', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">请求城市</span>
              <Input
                className="w-[140px]"
                placeholder="requested_city"
                value={getFilterText('requested_city')}
                onChange={(event) => setFilterValue('requested_city', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">真实省份</span>
              <Input
                className="w-[140px]"
                placeholder="province"
                value={getFilterText('province')}
                onChange={(event) => setFilterValue('province', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">真实城市</span>
              <Input
                className="w-[140px]"
                placeholder="city"
                value={getFilterText('city')}
                onChange={(event) => setFilterValue('city', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">请求 code</span>
              <Input
                className="w-[140px]"
                placeholder="requested_code"
                value={getFilterText('requested_code')}
                onChange={(event) => setFilterValue('requested_code', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">出口 IP</span>
              <Input
                className="w-[150px]"
                placeholder="location_check_ip"
                value={getFilterText('location_check_ip')}
                onChange={(event) => setFilterValue('location_check_ip', event.target.value)}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">验证状态</span>
              <Select
                value={getFilterText('verification_status') || 'all'}
                onValueChange={(value) => {
                  setFilterValue(
                    'verification_status',
                    value === 'all' ? undefined : value,
                  )
                }}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="pending">待验证</SelectItem>
                  <SelectItem value="verified">已验证</SelectItem>
                  <SelectItem value="verified_mismatch">已验证，位置不一致</SelectItem>
                  <SelectItem value="failed">验证失败，待重试</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">位置匹配</span>
              <Select
                value={filters.location_matched === undefined ? 'all' : String(filters.location_matched)}
                onValueChange={(value) => {
                  setFilterValue(
                    'location_matched',
                    value === 'all' ? undefined : value === 'true',
                  )
                }}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="true">一致</SelectItem>
                  <SelectItem value="false">不一致</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <Button variant="outline" onClick={resetFilters}>
              <RotateCcw className="h-4 w-4 mr-2" />
              重置
            </Button>
          </div>
        </div>

        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[150px]">账号</TableHead>
                <TableHead className="min-w-[170px]">请求位置</TableHead>
                <TableHead className="min-w-[170px]">真实出口位置</TableHead>
                <TableHead className="min-w-[180px]">代理内容</TableHead>
                <TableHead className="min-w-[110px]">地址</TableHead>
                <TableHead className="min-w-[170px]">验证状态</TableHead>
                <TableHead className="min-w-[160px]">出口校验</TableHead>
                <TableHead className="min-w-[170px]">验证来源</TableHead>
                <TableHead className="min-w-[140px]">出口 IP</TableHead>
                <TableHead className="min-w-[110px]">城市 code</TableHead>
                <TableHead className="min-w-[180px]">运营商/归属</TableHead>
                <TableHead className="min-w-[260px]">备注</TableHead>
                <TableHead className="min-w-[190px]">时间</TableHead>
                <TableHead className="sticky right-0 min-w-[190px] border-l bg-background">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center text-muted-foreground py-8">
                    {loading ? '加载中...' : '暂无数据'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow key={rowKey(row, index)}>
                    <TableCell className="align-top">
                      <div className="space-y-1">
                        <div className="font-semibold">
                          {displayValue(row.account_name)}
                        </div>
                        <div className="flex flex-wrap gap-1 text-xs">
                          {hasValue(row.is_rd) && (
                            <Badge variant="secondary">
                              {toBoolean(row.is_rd) ? 'RD' : '普通'}
                            </Badge>
                          )}
                          {hasValue(row.protocol) && (
                            <Badge variant="outline">
                              protocol {displayValue(row.protocol)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-1">
                        <div className="font-medium">
                          {locationText(
                            row.requested_province,
                            row.requested_city,
                            row.requested_code,
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          requested_code: {displayValue(row.requested_code)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-1">
                        <div className="font-medium">
                          {locationText(
                            row.province,
                            row.city,
                            row.location_check_city_code,
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          province / city
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top font-mono text-xs">
                      <div className="max-w-[220px] whitespace-pre-wrap break-words">
                        {displayValue(row.content)}
                      </div>
                      {hasValue(row.apk_key_duration) && (
                        <div className="mt-1 text-muted-foreground">
                          duration: {displayValue(row.apk_key_duration)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top font-mono text-xs">
                      {displayValue(row.address)}
                    </TableCell>
                    <TableCell className="align-top">{renderVerificationStatus(row)}</TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-1.5 text-xs">
                        {hasValue(row.location_verified) ? (
                          <Badge variant="outline">
                            {toBoolean(row.location_verified) ? '位置验证完成' : '位置验证未完成'}
                          </Badge>
                        ) : (
                          <Badge variant="outline">未记录</Badge>
                        )}
                        {hasValue(row.location_matched) && (
                          <div className={toBoolean(row.location_matched) ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            {toBoolean(row.location_matched) ? '出口已通过' : '出口未通过'}
                          </div>
                        )}
                        <div className="text-muted-foreground">
                          置信度：{displayValue(row.location_confidence)}
                        </div>
                        {hasValue(row.location_region_matched) && (
                          <div className="text-muted-foreground">
                            {toBoolean(row.location_region_matched) ? '匹配请求地区' : '不匹配请求地区'}
                          </div>
                        )}
                        {hasValue(row.location_region_consistent) && (
                          <div className="text-muted-foreground">
                            {toBoolean(row.location_region_consistent) ? '各源地区一致' : '各源地区不一致'}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-1 text-xs">
                        <div>{displayValue(row.location_check_source)}</div>
                        <div className="text-muted-foreground">
                          已查 {displayValue(row.location_check_sources_checked)} / {displayValue(row.location_check_sources_total)}
                        </div>
                        <div className="text-muted-foreground">
                          出口一致 {displayValue(row.location_check_sources_matched)}
                        </div>
                        <div className="text-muted-foreground">
                          请求地区匹配 {displayValue(row.location_requested_region_sources_matched)}
                        </div>
                        {Boolean(row.location_check_errors?.length) && (
                          <div className="text-red-600 dark:text-red-400">
                            来源错误 {row.location_check_errors?.length}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="align-top font-mono text-xs">
                      {displayValue(row.location_check_ip)}
                    </TableCell>
                    <TableCell className="align-top font-mono text-xs">
                      {displayValue(row.location_check_city_code)}
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="max-w-[220px] whitespace-pre-wrap break-words">
                        {displayValue(row.location_check_isp)}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <pre className="max-w-[320px] whitespace-pre-wrap break-words rounded bg-muted px-3 py-2 text-xs leading-relaxed">
                        {displayValue(row.location_remark)}
                      </pre>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div>创建：{displayValue(row.create_time)}</div>
                        <div>最近验证：{displayValue(row.last_validation_time)}</div>
                        <div>验证完成：{displayValue(row.verified_time)}</div>
                      </div>
                    </TableCell>
                    <TableCell className="sticky right-0 border-l bg-background align-top">
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={loading || !row.id || toBoolean(row.location_matched) || Boolean(actionLoadingKey)}
                          onClick={() => setPendingAction({ action: 'pass', row })}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          {toBoolean(row.location_matched) ? '已通过' : '标记通过'}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={loading || !row.id || row.account_name?.endsWith('_remove') || Boolean(actionLoadingKey)}
                          onClick={() => setPendingAction({ action: 'remove', row })}
                        >
                          <UserMinus className="h-4 w-4" />
                          {row.account_name?.endsWith('_remove') ? '已追加后缀' : '追加 _remove'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            共 {total} 条记录
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pageNum === 1 || loading}
              onClick={() => setPageNum((current) => Math.max(1, current - 1))}
            >
              上一页
            </Button>
            <span className="text-sm">
              第 {pageNum} 页 / 共 {totalPages} 页
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pageNum >= totalPages || loading}
              onClick={() => setPageNum((current) => current + 1)}
            >
              下一页
            </Button>
            <select
              className="border rounded px-2 py-1 text-sm bg-background"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value))
                setPageNum(1)
              }}
            >
              <option value={10}>10 条/页</option>
              <option value={20}>20 条/页</option>
              <option value={50}>50 条/页</option>
              <option value={100}>100 条/页</option>
            </select>
          </div>
        </div>

        <AlertDialog
          open={Boolean(pendingAction)}
          onOpenChange={(open) => {
            if (!open && !actionLoadingKey) {
              setPendingAction(null)
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingAction ? actionConfig[pendingAction.action].title : '确认操作'}
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  {pendingAction ? actionConfig[pendingAction.action].description : ''}
                </span>
                <span className="block font-mono text-xs">
                  账号：{displayValue(pendingAction?.row.account_name)}
                </span>
                <span className="block font-mono text-xs">
                  ID：{displayValue(pendingAction?.row.id)}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={Boolean(actionLoadingKey)}>取消</AlertDialogCancel>
              <AlertDialogAction
                disabled={Boolean(actionLoadingKey)}
                className={pendingAction?.action === 'remove' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
                onClick={(event) => {
                  event.preventDefault()
                  void handleConfirmAction()
                }}
              >
                {actionLoadingKey ? '处理中...' : '确认'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
